<template>
  <div class="bug-report-wrapper">
    <!-- Floating trigger button – mit Beschriftung, damit klar ist, wofür
         der Käfer steht (Kundenfeedback 13.10.26). -->
    <v-btn
      class="bug-fab"
      rounded="pill"
      height="44"
      color="#1a1b2e"
      elevation="4"
      :aria-label="$gt('Fehler melden')"
      @click="openDialog()"
    >
      <Bug :size="18" color="#fff" class="mr-2" />
      <span class="bug-fab-label">{{ $gt('Fehler melden') }}</span>
    </v-btn>

    <!-- Bug report dialog -->
    <v-dialog v-model="visible" persistent max-width="620" class="bug-report-overlay">
      <v-card rounded="lg" class="bug-dialog" :style="dragStyle">
        <!-- Header -->
        <v-card-title
          class="bug-header d-flex align-center justify-space-between"
          :style="{ cursor: drag.active ? 'grabbing' : 'grab' }"
          @mousedown.prevent="startDrag"
        >
          <div class="d-flex align-center">
            <Bug :size="20" class="mr-2" />
            <span>{{ $gt('Fehler melden / Verbesserungsvorschlag einreichen') }}</span>
          </div>
          <v-btn icon variant="text" size="small" @click="closeDialog" :disabled="submitting">
            <X :size="18" />
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-5">
          <!-- Title -->
          <v-text-field
            v-model="form.title"
            :placeholder="$gt('Kurze Zusammenfassung des Fehlers')"
            variant="outlined"
            density="comfortable"
            :rules="[v => !!v || 'Titel ist erforderlich']"
            class="mb-1"
          >
            <template #label>
              <RequiredLabel :label="$gt('Titel')" required />
            </template>
          </v-text-field>

          <!-- Description -->
          <v-textarea
            v-model="form.description"
            :label="$gt('Beschreibung')"
            :placeholder="$gt('Was ist passiert? Was haben Sie erwartet?')"
            variant="outlined"
            density="comfortable"
            rows="4"
            class="mb-1"
          />

          <!-- Screenshot preview -->
          <div v-if="screenshotUrl" class="screenshot-section mb-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-caption font-weight-medium" style="color: #64748b;">
                <Camera :size="14" class="mr-1" style="vertical-align: -2px;" />
                {{ $gt('Screenshot (automatisch)') }}
              </span>
              <v-btn variant="text" size="x-small" color="grey" @click="retakeScreenshot">
                <RefreshCw :size="14" class="mr-1" />
                {{ $gt('Neu aufnehmen') }}
              </v-btn>
            </div>
            <div class="screenshot-preview">
              <img :src="screenshotUrl" :alt="$gt('Screenshot')" />
            </div>
          </div>
          <div v-else-if="screenshotLoading" class="d-flex align-center justify-center pa-4 mb-4">
            <v-progress-circular indeterminate size="24" width="2" color="primary" class="mr-2" />
            <span class="text-caption" style="color: #94a3b8;">{{ $gt('Screenshot wird erstellt…') }}</span>
          </div>

          <!-- System info (collapsed by default) -->
          <v-expansion-panels variant="accordion" class="sys-info-panels">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-caption font-weight-medium" style="min-height: 40px; padding: 8px 12px;">
                <Monitor :size="14" class="mr-2" style="color: #64748b;" />
                {{ $gt('Systemdaten (automatisch erfasst)') }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="sys-info-grid">
                  <div class="sys-info-row">
                    <span class="sys-label">{{ $gt('URL') }}</span>
                    <span class="sys-value">{{ systemInfo.url }}</span>
                  </div>
                  <div class="sys-info-row alt">
                    <span class="sys-label">{{ $gt('Browser') }}</span>
                    <span class="sys-value">{{ systemInfo.browser }}</span>
                  </div>
                  <div class="sys-info-row">
                    <span class="sys-label">{{ $gt('Bildschirm') }}</span>
                    <span class="sys-value">{{ systemInfo.screenSize }}</span>
                  </div>
                  <div class="sys-info-row alt">
                    <span class="sys-label">{{ $gt('Benutzer') }}</span>
                    <span class="sys-value">{{ systemInfo.userName }}</span>
                  </div>
                  <div class="sys-info-row">
                    <span class="sys-label">{{ $gt('Rolle') }}</span>
                    <span class="sys-value">{{ systemInfo.userRole }}</span>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4 d-flex justify-end">
          <v-btn variant="text" @click="closeDialog" :disabled="submitting">{{ $gt('Abbrechen') }}</v-btn>
          <v-btn
            variant="flat"
            color="#0076e5"
            :loading="submitting"
            :disabled="!form.title.trim()"
            @click="submit"
          >
            <Send :size="16" class="mr-1" />
            {{ $gt('Absenden') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Bug, X, Camera, RefreshCw, Monitor, Send } from "lucide-vue-next";
import html2canvas from "html2canvas";
import { postRequestGeneric } from "@/services/backendApiGeneric";
import { useAuthStore } from "@/stores/authStore";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { useUiStore } from "@/stores/uiStore";
import RequiredLabel from "@/components/inputs/RequiredLabel.vue";

export default {
  name: "BugReportButton",

  components: { Bug, X, Camera, RefreshCw, Monitor, Send, RequiredLabel },

  data() {
    return {
      visible: false,
      submitting: false,
      drag: {
        active: false,
        startX: 0,
        startY: 0,
        offsetX: 0,
        offsetY: 0,
      },
      screenshotUrl: null,
      screenshotBlob: null,
      screenshotLoading: false,
      form: {
        title: "",
        description: "",
      },
      systemInfo: {
        url: "",
        browser: "",
        screenSize: "",
        userAgent: "",
        userName: "",
        userRole: "",
      },
    };
  },

  computed: {
    bugReportRequest() {
      return useUiStore().bugReportRequest;
    },
    dragStyle() {
      return {
        transform: `translate(${this.drag.offsetX}px, ${this.drag.offsetY}px)`,
        userSelect: this.drag.active ? 'none' : '',
      };
    },
  },

  watch: {
    // „Support"-Link in Fehlermeldungen öffnet diesen Dialog vorbelegt
    // (Kundenfeedback 16.10.2026, II).
    bugReportRequest(request) {
      if (request) this.openDialog(request);
    },
  },
  beforeUnmount() {
    this.stopDrag();
  },

  methods: {
    startDrag(e) {
      if (e.button !== 0) return;
      this.drag.active = true;
      this.drag.startX = e.clientX - this.drag.offsetX;
      this.drag.startY = e.clientY - this.drag.offsetY;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    },

    onDrag(e) {
      if (!this.drag.active) return;
      this.drag.offsetX = e.clientX - this.drag.startX;
      this.drag.offsetY = e.clientY - this.drag.startY;
    },

    stopDrag() {
      this.drag.active = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },

    async openDialog(prefill = null) {
      this.form = {
        title: prefill?.title ?? "",
        description: prefill?.description ?? "",
      };
      this.screenshotUrl = null;
      this.screenshotBlob = null;
      this.drag.offsetX = 0;
      this.drag.offsetY = 0;
      this.collectSystemInfo();
      this.visible = true;
      await this.captureScreenshot();
    },

    closeDialog() {
      this.visible = false;
      if (this.screenshotUrl) {
        URL.revokeObjectURL(this.screenshotUrl);
        this.screenshotUrl = null;
      }
    },

    collectSystemInfo() {
      const auth = useAuthStore();
      this.systemInfo = {
        url: window.location.href,
        browser: this.parseBrowser(navigator.userAgent),
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent,
        userName: `${auth.firstName} ${auth.lastName}`.trim() || auth.userName,
        userRole: auth.userRole,
      };
    },

    parseBrowser(ua) {
      if (ua.includes("Firefox/")) return "Firefox " + ua.split("Firefox/")[1].split(" ")[0];
      if (ua.includes("Edg/")) return "Edge " + ua.split("Edg/")[1].split(" ")[0];
      if (ua.includes("Chrome/")) return "Chrome " + ua.split("Chrome/")[1].split(" ")[0];
      if (ua.includes("Safari/") && !ua.includes("Chrome")) return "Safari " + ua.split("Version/")[1]?.split(" ")[0] || "";
      return "Unbekannt";
    },

    async captureScreenshot() {
      this.screenshotLoading = true;
      try {
        // Small delay to let dialog open so html2canvas doesn't capture it mid-render
        await new Promise((r) => setTimeout(r, 100));

        const canvas = await html2canvas(document.body, {
          useCORS: true,
          scale: Math.min(window.devicePixelRatio, 2),
          ignoreElements: (el) => {
            // Skip only the bug-report dialog itself (and its overlay/scrim),
            // NOT other open modals/overlays — those should be visible in the screenshot.
            if (el.classList?.contains?.("bug-report-overlay")) return true;
            if (el.closest?.(".bug-report-overlay")) return true;
            if (el.closest?.(".bug-dialog")) return true;
            // Scrim of the bug-report overlay only
            if (
              el.classList?.contains?.("v-overlay__scrim") &&
              el.parentElement?.classList?.contains?.("bug-report-overlay")
            ) {
              return true;
            }
            return false;
          },
        });

        canvas.toBlob((blob) => {
          if (blob) {
            this.screenshotBlob = blob;
            this.screenshotUrl = URL.createObjectURL(blob);
          }
          this.screenshotLoading = false;
        }, "image/png");
      } catch (e) {
        console.warn("Screenshot capture failed:", e);
        this.screenshotLoading = false;
      }
    },

    async retakeScreenshot() {
      if (this.screenshotUrl) URL.revokeObjectURL(this.screenshotUrl);
      this.screenshotUrl = null;
      this.screenshotBlob = null;
      await this.captureScreenshot();
    },

    async submit() {
      if (!this.form.title.trim()) return;

      this.submitting = true;
      const snackbar = useSnackbarStore();

      try {
        const formData = new FormData();
        formData.append("title", this.form.title.trim());
        formData.append("description", this.form.description.trim());
        formData.append("url", this.systemInfo.url);
        formData.append("browser", this.systemInfo.browser);
        formData.append("screenSize", this.systemInfo.screenSize);
        formData.append("userAgent", this.systemInfo.userAgent);

        if (this.screenshotBlob) {
          formData.append("screenshot", this.screenshotBlob, "screenshot.png");
        }

        await postRequestGeneric("/protected/bug-report", formData, true);
        snackbar.showSuccess("Fehlermeldung wurde erfolgreich gesendet. Vielen Dank!");
        this.closeDialog();
      } catch (e) {
        snackbar.showError("Fehlermeldung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.bug-fab {
  position: fixed;
  /* --app-fab-lift: Höhe einer fest stehenden Fußleiste am unteren Rand
     (setzt die Fragebogen-Hülle in der Mobil-Ansicht), sonst 0. */
  bottom: calc(24px + var(--app-fab-lift, 0px));
  right: 24px;
  z-index: 10000;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  pointer-events: auto;
}
.bug-fab-label {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}
.bug-fab:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(26, 27, 46, 0.35) !important;
}

.bug-header {
  background: #1a1b2e;
  color: #fff;
  font-size: 16px;
  padding: 14px 20px;
  cursor: grab;
  user-select: none;
}

.screenshot-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  background: #f8fafc;
}
.screenshot-preview {
  border-radius: 6px;
  overflow: hidden;
  max-height: 200px;
  cursor: pointer;
}
.screenshot-preview img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  max-height: 200px;
}

.sys-info-panels :deep(.v-expansion-panel) {
  border: 1px solid #e5e7eb;
  border-radius: 8px !important;
  box-shadow: none;
}
.sys-info-panels :deep(.v-expansion-panel-title) {
  font-size: 13px;
  color: #64748b;
}

.sys-info-grid {
  font-size: 13px;
}
.sys-info-row {
  display: flex;
  padding: 6px 0;
}
.sys-info-row.alt {
  background: #f8fafc;
  border-radius: 4px;
  margin: 0 -8px;
  padding: 6px 8px;
}
.sys-label {
  color: #64748b;
  font-weight: 600;
  width: 100px;
  flex-shrink: 0;
}
.sys-value {
  color: #334155;
  word-break: break-all;
}
</style>
